/**
 * useServiceOrders — TanStack Query data layer for the service-order module.
 *
 * A service order (OS) is the bridge between an approved quote and the executed
 * job. It can be born from a quote (quote_id) or created standalone. Like quotes,
 * it carries child items and photos; offline it keeps them inline on the cached
 * row (_offline_items / _offline_photos) so the whole module renders offline.
 *
 * Offline mutations are delegated to useOfflineSync, which enqueues the op and
 * updates the local cache. See [[orcamigo-offline-sync-uuid-bug]] for why we
 * never let an offline_ id reach Postgres.
 */
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { checkNetworkStatus } from '@/hooks/useNetworkStatus';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { cacheServiceOrders, getCachedServiceOrders } from '@/lib/offlineDataCache';
import { ensureStorageUrl } from '@/lib/offlineSync/storageUpload';
import { serviceOrderItemUnitCost } from '@/lib/serviceOrderItems';
import { toDiagnosisColumns } from '@/lib/serviceOrderDiagnosis';
import { toAssetColumns } from '@/lib/serviceOrderAsset';
import { buildServiceOrderHeaderColumns } from '@/lib/serviceOrderHeader';
import { buildQuoteTotalsColumns, quoteItemLineTotal } from '@/lib/quoteTotals';
import { prepareDocumentStockReturn } from '@/lib/stockDocumentReturn';
import { runDocumentDeletion } from '@/lib/documentDeletion';
import type { Database } from '@/integrations/supabase/types';
import { toast } from 'sonner';

type ServiceOrderItemInsert = Database['public']['Tables']['service_order_items']['Insert'];
type ServiceOrderPhotoInsert = Database['public']['Tables']['service_order_photos']['Insert'];

const ORDERS_KEY = 'service-orders';
const PHOTO_BUCKET = 'quote-photos';
const PHOTO_FOLDER = 'service-orders';

export type ServiceOrderStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export const SERVICE_ORDER_STATUSES: { value: ServiceOrderStatus; label: string }[] = [
  { value: 'pending', label: 'Pendente' },
  { value: 'in_progress', label: 'Em andamento' },
  { value: 'completed', label: 'Concluída' },
  { value: 'cancelled', label: 'Cancelada' },
];

export interface ServiceOrderItemInput {
  name: string;
  description?: string | null;
  quantity?: number | null;
  unit_price?: number;
  discount?: number | null;
  discount_type?: string | null;
  item_type?: string | null;
  unit?: string | null;
  image_url?: string | null;
  product_id?: string | null;
  barcode?: string | null;
  total?: number;
  position?: number | null;
  /** Snapshot do custo — ver `src/lib/serviceOrderItems.ts` (DECISÃO 2, grupo 5). */
  unit_cost?: number | string | null;
  /** Serviço-kit que trouxe este material — ver `src/lib/productBundles.ts`. */
  bundle_parent_product_id?: string | null;
}

export interface ServiceOrderPhotoInput {
  image_url?: string | null;
  imageUrl?: string | null;
  title?: string | null;
  description?: string | null;
  position?: number | null;
}

export interface ServiceOrderInput {
  quote_id?: string | null;
  client_id?: string | null;
  client_name?: string | null;
  client_phone?: string | null;
  client_address?: string | null;
  client_document?: string | null;
  client_email?: string | null;
  title?: string | null;
  description?: string | null;
  status?: ServiceOrderStatus;
  scheduled_date?: string | null;
  /** Previsão de ENTREGA (com hora) — ver `src/lib/serviceOrderHeader.ts`. */
  expected_date?: string | null;
  completed_at?: string | null;
  /**
   * ⚠️ CAMPO DE ENTRADA IGNORADO na gravação: o total é sempre RECALCULADO por
   * `buildQuoteTotalsColumns` a partir dos itens + desconto + acréscimo, que é
   * a mesma função do orçamento (CLAUDE.md #69). Continua no tipo porque os
   * chamadores o montam, mas quem manda é a conta — um total recebido pronto
   * poderia divergir do que o PDF imprime, e o documento seria a única prova.
   */
  total?: number;
  /**
   * Ajustes do valor final — MESMAS colunas e MESMA conta do orçamento (#69).
   * Já normalizados: quem os monta é `buildQuoteTotalsColumns`, e é ele que
   * transforma o texto do formulário em número (o campo é pt-BR, e um `Number`
   * cru sobre "1.500,00" daria R$ 1,50 — #151a).
   */
  discount?: number | null;
  discount_type?: 'value' | 'percent' | string | null;
  shipping?: number | null;
  show_sequence_id?: boolean;
  /** Diagnóstico técnico — ver `src/lib/serviceOrderDiagnosis.ts`. */
  defect?: string | null;
  diagnosis?: string | null;
  solution?: string | null;
  /** Equipamento/objeto — ver `src/lib/serviceOrderAsset.ts`. */
  asset_type?: string | null;
  asset_brand?: string | null;
  asset_identifier?: string | null;
  asset_accessories?: string | null;
  asset_condition?: string | null;
  /** Responsáveis, pagamento e garantia — ver `src/lib/serviceOrderHeader.ts`. */
  attendant_name?: string | null;
  technician_name?: string | null;
  payment_methods?: string[] | null;
  payment_terms?: string | null;
  warranty_amount?: number | null;
  warranty_unit?: string | null;
  terms_conditions?: string | null;
  /** Assinatura do cliente na entrega (coletada na tela de detalhe). */
  client_signature?: string | null;
  client_signer_name?: string | null;
  client_signed_at?: string | null;
  items?: ServiceOrderItemInput[];
  photos?: ServiceOrderPhotoInput[];
}

/** Items of an order, whether it's a synced row (joined) or an offline temp. */
export function getOrderItems(order: any): any[] {
  if (!order) return [];
  if (Array.isArray(order.service_order_items)) return order.service_order_items;
  if (Array.isArray(order._offline_items)) return order._offline_items;
  return [];
}

/** Photos of an order, whether it's a synced row (joined) or an offline temp. */
export function getOrderPhotos(order: any): any[] {
  if (!order) return [];
  if (Array.isArray(order.service_order_photos)) return order.service_order_photos;
  if (Array.isArray(order._offline_photos)) return order._offline_photos;
  return [];
}

// ===== Read =====

/**
 * Base query: every service order for the active company, with child items and
 * photos joined. Cached locally so the module keeps working offline.
 */
export function useServiceOrders() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [ORDERS_KEY, companyId],
    enabled: Boolean(user?.id && companyId),
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    // Revalida a cada montagem, como as irmãs `useContracts`/`useReceipts`. Sem
    // isto, `initialData` + `staleTime` serviam o cache como fresco e uma OS
    // criada em OUTRO dispositivo não aparecia na lista nem abria pelo id — a
    // tela dizia "Ordem de serviço não encontrada" (Addendum do #80).
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => (user?.id ? getCachedServiceOrders(user.id) ?? undefined : undefined),
    queryFn: async (): Promise<any[]> => {
      if (!user?.id || !companyId) return [];
      const { data, error } = await supabase
        .from('service_orders')
        .select('*, service_order_items(*), service_order_photos(*)')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const orders = (data ?? []) as any[];
      cacheServiceOrders(user.id, orders);
      return orders;
    },
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [ORDERS_KEY, companyId] });
  }, [queryClient, companyId]);

  const syncFromCache = useCallback(() => {
    if (!user?.id) return;
    queryClient.setQueryData([ORDERS_KEY, companyId], getCachedServiceOrders(user.id) ?? []);
  }, [queryClient, user?.id, companyId]);

  return { ...query, orders: query.data ?? [], invalidate, syncFromCache };
}

/*
 * `useServiceOrdersFiltered` foi REMOVIDO quando `/ordens-servico` adotou o
 * desenho oficial. A tela passou a precisar do recorte SEM o status (é dele que
 * saem as contagens de cada botão da faixa), então ela consome `useServiceOrders`
 * e aplica o recorte por `matchesServiceOrderStatus` — que é o mesmo predicado do
 * resumo do hero, incluindo o default `status ?? 'pending'`. Mantê-lo aqui seria
 * uma SEGUNDA implementação da mesma regra, sem consumidor e sem teste próprio:
 * a espécie de código morto que o #187 descreve.
 */

/** A single order by id, from the cached list (works offline). */
export function useServiceOrder(id: string | undefined) {
  const raw = useServiceOrders();
  const order = useMemo(
    () => (id ? raw.orders.find((o: any) => o.id === id) ?? null : null),
    [raw.orders, id],
  );
  return { ...raw, order };
}

// ===== Mutations =====

function toDbItem(raw: ServiceOrderItemInput, position: number): Record<string, any> {
  const discountType = raw.discount_type || 'value';
  const quantity = Number(raw.quantity ?? 1);
  const unitPrice = Number(raw.unit_price ?? 0);
  const discount = Number(raw.discount ?? 0);
  // O total da LINHA sai da fonte única do dinheiro (#69): arredondado em
  // centavos e com o desconto limitado à base. A conta local que existia aqui
  // não arredondava, então a soma das linhas impressas no PDF podia divergir do
  // total do rodapé por centavos — o erro que o cliente encontra e o app não.
  const computedTotal = quoteItemLineTotal(raw as never);
  return {
    name: String(raw.name ?? ''),
    description: raw.description ?? null,
    quantity,
    unit_price: unitPrice,
    // Snapshot do custo (DECISÃO 2 do grupo 5): vem do item, NUNCA de uma
    // leitura nova de `products.cost`. Sem isto a OS ficaria fora do CMV e o
    // lucro pareceria maior do que é — em silêncio.
    unit_cost: serviceOrderItemUnitCost(raw),
    discount,
    discount_type: discountType,
    item_type: raw.item_type || 'service',
    unit: raw.unit ?? null,
    image_url: raw.image_url ?? null,
    product_id: raw.product_id ?? null,
    barcode: raw.barcode ?? null,
    // Origem do kit (grupo 10 / B1). Perder a marca aqui não dá erro nenhum:
    // a OS grava, e a próxima expansão do mesmo kit duplica os materiais.
    bundle_parent_product_id: raw.bundle_parent_product_id ?? null,
    total: Number.isFinite(raw.total) ? Number(raw.total) : Math.max(0, computedTotal),
    position: raw.position ?? position,
  };
}

export function useCreateServiceOrder() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { saveServiceOrderOffline } = useOfflineSync();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: ServiceOrderInput) => {
      if (!user?.id) throw new Error('Sem sessão');
      if (!companyId) throw new Error('Nenhuma empresa ativa');

      const items = input.items ?? [];
      const photos = input.photos ?? [];
      const online = await checkNetworkStatus();

      const orderFields = {
        quote_id: input.quote_id ?? null,
        client_id: input.client_id ?? null,
        client_name: input.client_name ?? null,
        client_phone: input.client_phone ?? null,
        client_address: input.client_address ?? null,
        client_document: input.client_document ?? null,
        title: input.title ?? null,
        description: input.description ?? null,
        status: input.status ?? 'pending',
        scheduled_date: input.scheduled_date ?? null,
        show_sequence_id: input.show_sequence_id ?? false,
        // O DINHEIRO (total + desconto + acréscimo) sai da MESMA função do
        // orçamento. Antes o total era só a soma dos itens, e uma OS gerada de
        // um orçamento com desconto de cabeçalho ou frete tinha um total
        // DIFERENTE do documento que a originou — sem erro nenhum (#69).
        ...buildQuoteTotalsColumns({
          items: items as never,
          discount: input.discount,
          discountType: input.discount_type,
          shipping: input.shipping,
        }),
        // As 3 colunas de uma vez — enumerá-las à mão aqui é como um caminho
        // esquece uma e o dado some sem erro (CLAUDE.md #33/#62).
        ...toDiagnosisColumns(input),
        // Idem para o equipamento (5) e o cabeçalho novo (9).
        ...toAssetColumns(input),
        ...buildServiceOrderHeaderColumns(input),
        // `completed_at` fica FORA dos builders: quem o carimba é a mudança de
        // status, não o formulário.
        completed_at: input.completed_at ?? null,
      };

      if (!online) {
        saveServiceOrderOffline({
          serviceOrder: orderFields,
          items,
          photos,
          userId: user.id,
          companyId,
        });
        queryClient.setQueryData([ORDERS_KEY, companyId], getCachedServiceOrders(user.id) ?? []);
        return { offline: true as const };
      }

      // Next per-company sequence.
      const { data: maxSeqRow } = await supabase
        .from('service_orders')
        .select('sequence_id')
        .eq('company_id', companyId)
        .not('sequence_id', 'is', null)
        .order('sequence_id', { ascending: false })
        .limit(1)
        .maybeSingle();
      const nextSequenceId = ((maxSeqRow as any)?.sequence_id ?? 0) + 1;

      const { data: order, error: orderError } = await supabase
        .from('service_orders')
        .insert({ ...orderFields, user_id: user.id, company_id: companyId, sequence_id: nextSequenceId })
        .select('*')
        .single();
      if (orderError) throw orderError;

      if (items.length > 0) {
        const rows: Record<string, any>[] = [];
        for (let i = 0; i < items.length; i++) {
          const base = toDbItem(items[i], i);
          base.image_url = await ensureStorageUrl(base.image_url, PHOTO_BUCKET, PHOTO_FOLDER, user.id);
          rows.push({ ...base, service_order_id: (order as any).id });
        }
        const { error: itemsError } = await supabase.from('service_order_items').insert(rows as ServiceOrderItemInsert[]);
        if (itemsError) {
          await supabase.from('service_order_items').delete().eq('service_order_id', (order as any).id);
          await supabase.from('service_orders').delete().eq('id', (order as any).id);
          throw itemsError;
        }
      }

      if (photos.length > 0) {
        const rows: Record<string, any>[] = [];
        for (let i = 0; i < photos.length; i++) {
          const rawUrl = photos[i].image_url ?? photos[i].imageUrl ?? null;
          const url = await ensureStorageUrl(rawUrl, PHOTO_BUCKET, PHOTO_FOLDER, user.id);
          if (!url) continue;
          rows.push({
            service_order_id: (order as any).id,
            user_id: user.id,
            image_url: url,
            title: photos[i].title ?? null,
            description: photos[i].description ?? null,
            position: photos[i].position ?? i,
          });
        }
        if (rows.length > 0) {
          const { error: photosError } = await supabase.from('service_order_photos').insert(rows as ServiceOrderPhotoInsert[]);
          if (photosError) {
            await supabase.from('service_order_items').delete().eq('service_order_id', (order as any).id);
            await supabase.from('service_order_photos').delete().eq('service_order_id', (order as any).id);
            await supabase.from('service_orders').delete().eq('id', (order as any).id);
            throw photosError;
          }
        }
      }

      queryClient.invalidateQueries({ queryKey: [ORDERS_KEY, companyId] });
      return { offline: false as const, id: (order as any).id as string };
    },
    onError: () => toast.error('Não foi possível salvar a ordem de serviço.'),
  });
}

/** Update only the scalar fields of an order (status, dates, title, etc.). */
export function useUpdateServiceOrder() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { updateServiceOrderOffline } = useOfflineSync();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: { id: string; updates: Partial<ServiceOrderInput> }) => {
      if (!user?.id) throw new Error('Sem sessão');
      const online = await checkNetworkStatus();

      // Only scalar columns here — item/photo editing goes through create/replace.
      const { items: _items, photos: _photos, ...scalarUpdates } = input.updates;

      if (!online) {
        updateServiceOrderOffline({ serviceOrderId: input.id, updates: scalarUpdates, userId: user.id });
        queryClient.setQueryData([ORDERS_KEY, companyId], getCachedServiceOrders(user.id) ?? []);
        return;
      }

      let query = supabase.from('service_orders').update(scalarUpdates).eq('id', input.id);
      if (companyId) query = query.eq('company_id', companyId);
      const { error } = await query;
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: [ORDERS_KEY, companyId] });
    },
    onError: () => toast.error('Não foi possível atualizar a ordem de serviço.'),
  });
}

export function useDeleteServiceOrder() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { deleteServiceOrderOffline } = useOfflineSync();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    // ⚠️ A assinatura recebe um OBJETO, não o id solto, de propósito: foi o que
    // fez o `tsc -b` listar todos os call-sites quando a devolução de estoque
    // na exclusão entrou (CLAUDE.md #71). Trocar só o corpo teria deixado os
    // chamadores compilando e nunca devolvendo nada.
    mutationFn: async (input: { id: string; returnStock?: boolean }) => {
      if (!user?.id) throw new Error('Sem sessão');
      const id = input.id;
      const online = await checkNetworkStatus();

      if (!online) {
        deleteServiceOrderOffline({
          serviceOrderId: id,
          userId: user.id,
          companyId,
          returnStock: input.returnStock === true,
        });
        queryClient.setQueryData([ORDERS_KEY, companyId], getCachedServiceOrders(user.id) ?? []);
        return { returned: null };
      }

      // Calcular ANTES (o vínculo `stock_movements.service_order_id` é SET NULL
      // e some com o delete) e aplicar DEPOIS de o delete ter dado certo —
      // devolver antes escreveria no estoque de uma OS que continua viva.
      // Ver `src/lib/documentDeletion.ts`.
      const outcome = await runDocumentDeletion({
        prepareReturn: () =>
          input.returnStock && companyId
            ? prepareDocumentStockReturn(companyId, 'service_order', [id])
            : Promise.resolve(null),
        remove: async () => {
          let query = supabase.from('service_orders').delete().eq('id', id);
          if (companyId) query = query.eq('company_id', companyId);
          const { error } = await query;
          if (error) throw error;
        },
      });

      if (outcome.prepareFailed || outcome.returnFailed) {
        // A exclusão é o que o usuário pediu; barrá-la porque a devolução
        // falhou deixaria o app preso numa ação destrutiva já confirmada.
        toast.warning('A OS foi excluída, mas não foi possível devolver o estoque.');
      }

      queryClient.invalidateQueries({ queryKey: [ORDERS_KEY, companyId] });
      return { returned: outcome.returned };
    },
    onError: () => toast.error('Não foi possível excluir a ordem de serviço.'),
  });
}
